
import React from 'react';
import { X, FileText, Check } from 'lucide-react';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import htmlDocx from 'html-docx-js/dist/html-docx';

interface DialogProps {
  text: {
    summary_title: string;
    summary_content: string;
  };
  sessionId: string | null;
  ingest_type: string;
  setPhase: (phase: string) => void;
  setConfirming: (confirming: boolean) => void;
  onSuccess: (isFinalPhase: boolean) => void;
  onSummaryGenerated: (summary: any, phase: string) => void;
}

const CustomizedDialogs: React.FC<DialogProps> = ({
  text,
  sessionId,
  ingest_type,
  setPhase,
  setConfirming,
  onSuccess,
  onSummaryGenerated
}) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

// const exportToWord = (elementId: string, fileName = 'summary.docx') => {
//   const element = document.getElementById(elementId);
//   if (!element) return;

//   const header = `
//     <html xmlns:o='urn:schemas-microsoft-com:office:office'
//           xmlns:w='urn:schemas-microsoft-com:office:word'
//           xmlns='http://www.w3.org/TR/REC-html40'>
//     <head><meta charset='utf-8'></head><body>`;
//   const footer = '</body></html>';

//   const html = header + element.innerHTML + footer;
//   const blob = htmlDocx.asBlob(html);
//   const url = URL.createObjectURL(blob);

//   const link = document.createElement('a');
//   link.href = url;
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
//   URL.revokeObjectURL(url);
// };


  const handleClose = () => {
    setOpen(false);
  };

  const confirmResponse = async () => {
    setLoading(true);
    try {
      if (!sessionId) {
        console.error('No session ID available for summary generation');
        return;
      }

      const summaryUrl = `${process.env.REACT_APP_API_BASE_URL}/post_interview_summary`;

      const payload = {
        "session_id": sessionId
      };

      const response = await axios.post(summaryUrl, payload, {
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
        },
      });

      console.log('Summary generated:', response.data);
      
      const newPhase = response.data.current_phase;
      setPhase(newPhase);
      setConfirming(true);
      setOpen(false);
      
      // Store the summary with metadata
      const summaryWithMetadata = {
        phase: ingest_type,
        title: text.summary_title,
        content: text.summary_content,
        timestamp: new Date().toISOString(),
        questionsAnswered: 0 // This should be passed from parent component
      };
      
      onSummaryGenerated(summaryWithMetadata, newPhase);
      
      // Check if it's the final phase
      const isFinalPhase = newPhase === 'final';
      onSuccess(isFinalPhase);
      
    } catch (error) {
      console.error('Error generating summary:', error);
      alert('Failed to submit response. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

 const formatMarkdownContent = (content: string) => {
  const tableRegex = /((?:\|.*\n)+)/g;
  const tablePlaceholder = '[[TABLE_BLOCK]]';
  const tableBlocks: string[] = [];

  // Extract and replace tables with placeholders
  content = content.replace(tableRegex, (match) => {
    const lines = match.trim().split('\n');
    if (lines.length < 2 || !lines[1].includes('---')) return match; // Not a table

    const headers = lines[0].split('|').map(h => h.trim()).filter(Boolean);
    const rows = lines.slice(2).map(line =>
      line.split('|').map(cell => cell.trim()).filter(Boolean)
    );

    const tableHtml = `
      <table style="width:100%; border-collapse: collapse; margin: 10px 0;">
        <thead>
          <tr>${headers.map(h => `<th style="border:1px solid #ccc; padding:6px; background:#f9f9f9;">${h}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${rows.map(row => `
            <tr>${row.map(cell => `<td style="border:1px solid #ccc; padding:6px;">${cell}</td>`).join('')}</tr>
          `).join('')}
        </tbody>
      </table>
    `;

    tableBlocks.push(tableHtml);
    return tablePlaceholder;
  });

  // Apply other markdown formatting
  let finalContent = content
    .replace(/^# (.*$)/gm, '<div class="text-2xl font-bold text-gray-900 mb-2">$1</div>')
    .replace(/^## (.*$)/gm, '<div class="text-xl font-semibold text-gray-800 mb-2 mt-4">$1</div>')
    .replace(/^### (.*$)/gm, '<div class="text-lg font-medium text-gray-700 mb-1 mt-1">$1</div>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="italic text-purple-700">$1</em>')
    .replace(/^- (.*$)/gm, '<li class="ml-6">$1</li>')
    .replace(/^---$/gm, '<hr class="my-6 border-gray-300">')
    .replace(/\n\n/g, '</p><p class="mb-2">')
    .replace(/\n/g, '<br />');

  // Put tables back in place
  tableBlocks.forEach(table => {
    finalContent = finalContent.replace(tablePlaceholder, table);
  });

  return finalContent;
};



  
const pdfdownload = () => {
  const element = document.getElementById('export-summary');
  if (!element) {
    console.error('Export element not found');
    return;
  }

  const clonedElement = element.cloneNode(true);
  clonedElement.style.display = 'block';
  document.body.appendChild(clonedElement);

  const options = {
  margin: 0.3,
  filename: 'summary.pdf',
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: { scale: 2, useCORS: true },
  jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
  pagebreak: {
    mode: ['avoid-all', 'css'],
    avoid: ['.page-break'],
  }
};


  html2pdf()
    .set(options)
    .from(clonedElement)
    .save()
    .then(() => {
      document.body.removeChild(clonedElement);
    })
    .catch((error) => {
      console.error('Error generating PDF:', error);
      document.body.removeChild(clonedElement);
    });
};

  console.log('CustomizedDialogs rendered with text:', text);
 
  if (!open) {
    return (
      <button
        onClick={handleClickOpen}
        className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <FileText className="w-5 h-5 relative z-10" />
        <span className="relative z-10 text-lg">View Summary</span>
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
      </button>
    );
  }

  return (
    <div style={{zIndex:"99999",backgroundColor:"rgba(0,0,0,0.8)"}} className="fixed inset-0  flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden animate-slideUp">
        {/* Header with gradient accent */}
        <div className="relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 to-purple-600"></div>
          <div className="flex items-center justify-between pt-6 pl-8 pr-8 pb-1 border-b border-gray-100">
            <div className="flex  gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                  {text.summary_title}
                </h2>
                <p className="text-sm text-gray-500 mt-1 text-left">Review and confirm your responses</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{border:"1px solid #bb1ccc",color:"#bb1ccc",marginTop:"-20px"}}
              className="p-1 hover:bg-purple-500 rounded-full transition-colors duration-200 group"
            >
              <X className="w-6 h-6 text-purple-400 group-hover:text-white" />
            </button>
          </div>
        </div>
<div id="export-summary" style={{ display: 'none',margin:"0" }}>
  <div className="bg-gray-50 rounded-3xl p-2 max-w-5xl mx-auto">
    <h2 className="text-2xl font-bold text-gray-900 mb-2">
      {text.summary_title}
    </h2>
    <div 
      className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
      dangerouslySetInnerHTML={{ 
        __html: formatMarkdownContent(text) 
      }}
    />
  </div>
</div>
        {/* Content with improved typography */}
        <div className="p-8 overflow-y-auto max-h-[60vh]">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
            <div 
              className="prose prose-lg max-w-none text-gray-700 leading-relaxed [&>strong]:text-gray-900 [&>strong]:font-semibold [&>em]:text-purple-700 [&>em]:not-italic [&>em]:font-medium text-left"
              dangerouslySetInnerHTML={{ 
                __html: formatMarkdownContent(text) 
              }}
            />
          </div>
        </div>

        {/* Footer with enhanced buttons */}
        <div className="flex items-center justify-between p-8 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="text-sm text-gray-500">
            Please review the summary carefully before confirming
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              style={{border:"1px solid #bb1ccc",color:"#bb1ccc"}}
              className="px-6 py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 hover:shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={pdfdownload}
              style={{ border: "1px solid #bb1ccc", color: "#bb1ccc" }}
              className="px-6 py-3 border-2 border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 hover:shadow-sm"
            >
              Download PDF
            </button>
       {/* <button
  onClick={() => exportToWord('export-summary', 'Interview_Summary.docx')}
  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
>
  Download as Word
</button> */}

            {/* <button
              onClick={confirmResponse}
              disabled={loading}
              className="group relative inline-flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none overflow-hidden"
            >
              <div style={{backgroundColor:"#bb1ccc"}} className="absolute inset-0  transition-opacity duration-300"></div>
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin relative z-10"></div>
                  <span className="relative z-10">Confirming Response...</span>
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 relative z-10" />
                  <span className="relative z-10">Confirm Your Response</span>
                </>
              )}
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizedDialogs;
